<template>
  <section id="ai" class="ai-studio">
    <div class="container">
      <div class="ai-studio__head">
        <div>
          <div class="section-heading">
            <div class="heading-line"/>
            <h2 class="section-title">AI Studio</h2>
          </div>
          <p class="section-subtitle">
            A working reference of my generative AI output: video, imagery, and the system prompts behind them.
          </p>
        </div>
      </div>

      <!-- Numbered so the three strands read as a deliberate set rather than
           three loose cards. -->
      <ol class="disciplines">
        <li v-for="(discipline, index) in aiDisciplines" :key="discipline.id">
          <!-- Two kinds of card, one shape. A strand backed by pictures shows
               one and leads to the gallery; only a strand whose artefact is
               text opens a panel, because that is the one thing that cannot be
               shown instead of described. -->
          <!-- A locked strand is a button, not a link: there is nowhere to go
               until access is granted, and an anchor would still expose the
               destination to a middle-click or a hover. -->
          <component
            :is="isGated(discipline) ? 'button' : NuxtLink"
            :type="isGated(discipline) ? 'button' : undefined"
            :to="isGated(discipline) ? undefined : (discipline.href || '/explore')"
            class="discipline"
            :class="{ 'has-media': discipline.image }"
            @click="isGated(discipline) ? (lockedStrand = discipline) : undefined"
          >
            <template v-if="discipline.image">
              <!-- Never plays off-screen: this panel is one of six on a track,
                   so without the observer below it would loop the whole time
                   somebody was reading a different section. The still stays on
                   as the poster so the card is never blank while it buffers. -->
              <!-- No src until the card is actually on screen. With one,
                   preload="metadata" was ignored and the whole 2.4MB clip
                   downloaded on every visit to the home page, for a card that
                   is off to the right of a horizontal track and paused. The
                   poster carries the card until then, which it did anyway. -->
              <video
                v-if="discipline.video"
                ref="videoEls"
                class="discipline__media"
                :data-src="discipline.video"
                :poster="thumb(discipline.image)"
                muted
                loop
                playsinline
                preload="none"
              />
              <img
                v-else
                :src="thumb(discipline.image)"
                alt=""
                class="discipline__media"
                loading="lazy"
                decoding="async"
              >
              <span class="discipline__veil"/>
            </template>

          <div class="discipline__top">
            <span class="discipline__index">{{ String(index + 1).padStart(2, '0') }}</span>
            <span class="discipline__icon">
              <svg v-if="discipline.id === 'video'" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                <rect x="2" y="4" width="14" height="16" rx="2"/>
                <path d="M16 10l6-3v10l-6-3z"/>
              </svg>
              <svg v-else-if="discipline.id === 'image'" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <circle cx="9" cy="9" r="1.8"/>
                <path d="M21 16l-5-5-6 6-3-3-4 4"/>
              </svg>
              <svg v-else viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 6l5 6-5 6"/>
                <line x1="12" y1="18" x2="20" y2="18"/>
              </svg>
            </span>
          </div>

          <h3 class="discipline__title">{{ discipline.label }}</h3>
          <p class="discipline__text">{{ discipline.description }}</p>

          <!-- Only shown while a card has no picture. A picture card is
               obviously interactive on its own, and a label inside one was a
               third copy of a phrase the section header already carries. -->
          <span v-if="!discipline.image" class="discipline__more">
            <span>{{ ctaFor(discipline) }}</span>
            <svg v-if="isGated(discipline)" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 21s-7-4.4-9.3-8.6A5.3 5.3 0 0 1 12 6.2a5.3 5.3 0 0 1 9.3 6.2C19 16.6 12 21 12 21z" />
            </svg>
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">
              <line x1="5" y1="12" x2="18" y2="12"/>
              <polyline points="13 7 18 12 13 17"/>
            </svg>
          </span>
          </component>
        </li>
      </ol>

      <!-- The work itself. Without this the section was three descriptors and
           a list of names — nothing you could actually look at. -->
      <div class="work">
        <div class="work__head">
          <span class="work__title">Recent generations</span>
          <NuxtLink to="/explore" class="work__all">View all</NuxtLink>
        </div>

        <!-- Nothing real until everything real is ready.
             Showing the compiled-in pictures first and swapping was the drift:
             a picture sat in the slot, the live row arrived, and it was pushed
             out from under the reader. These hold the same six slots at the
             same size, and the work replaces them in one frame. -->
        <ul v-if="!ready" class="work__list" aria-hidden="true">
          <li v-for="n in 6" :key="`strip-sk-${n}`" class="piece">
            <span class="piece__link piece__link--skeleton skeleton" />
          </li>
        </ul>

        <ul v-else class="work__list">
          <li
            v-for="piece in stripPieces"
            :key="piece.id"
            class="piece"
            @pointerenter="sizePop"
          >
            <!-- Carries which piece was clicked, so the gallery can open on
                 it rather than at the beginning. A query rather than a hash:
                 hashes are stripped from the address bar site-wide. -->
            <NuxtLink :to="{ path: '/explore', query: { piece: piece.id } }" class="piece__link">
              <!-- Lazy because the home page is a horizontal track: all six panels
                 are in the DOM from the first byte, so without this every image
                 on every panel is fetched and decoded before anyone has scrolled
                 anywhere. decoding="async" keeps the decode off the main thread,
                 which is what was making the scroll stutter as each panel
                 arrived. -->
              <img
                v-if="piece.image"
                :src="thumb(piece.image)"
                :alt="piece.title"
                class="piece__image"
                loading="lazy"
                decoding="async"
              >
              <span v-else class="piece__placeholder">{{ piece.title.charAt(0) }}</span>
              <span class="piece__meta">
                <span class="piece__name">{{ piece.title }}</span>
              </span>
            </NuxtLink>

            <!-- The hover preview. A separate layer rather than a transform on
                 the card: the card is a fixed 4/3 slot that crops with
                 object-fit, so nothing done to it could reveal the picture's
                 real shape. This sits on top, keeps the slot exactly where it
                 was, and is sized in JS to the image's own ratio.
                 pointer-events: none so hover stays with the card underneath
                 and the two cannot fight each other. -->
            <span v-if="piece.image" class="piece__pop" aria-hidden="true">
              <img :src="thumb(piece.image)" alt="" class="piece__pop-image" loading="lazy" decoding="async">
              <span class="piece__pop-meta">
                <span class="piece__name">{{ piece.title }}</span>
              </span>
            </span>
          </li>
        </ul>
      </div>

      <div class="tools">
        <div class="tools__label">
          <span class="tools__title">Tools &amp; Partners</span>
          <span class="tools__note">Platforms I create with, several of them ongoing partnerships</span>
        </div>

        <ul class="tools__list">
          <li v-for="tool in aiTools" :key="tool.name">
            <component
              :is="tool.url ? 'a' : 'span'"
              :href="tool.url || undefined"
              :target="tool.url ? '_blank' : undefined"
              :rel="tool.url ? 'noopener noreferrer' : undefined"
              class="tool"
              :class="{ 'is-static': !tool.url }"
            >
              <!-- Falls back to a lettermark when no logo has been sourced. -->
              <img
                v-if="tool.logo"
                :src="tool.logo"
                :alt="tool.name"
                class="tool__logo"
                loading="lazy"
                decoding="async"
              >
              <span v-else class="tool__mark">{{ tool.name.charAt(0) }}</span>
              <span class="tool__name">{{ tool.name }}</span>
            </component>
          </li>
        </ul>
      </div>
    </div>

    <LockedContentModal
      :item="lockedStrand ? { kind: 'Character sheets and system prompts', href: lockedStrand.href } : null"
      :locked="libraryLocked"
      @close="lockedStrand = null"
    />

  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, resolveComponent } from 'vue';
import { aiDisciplines, aiGenerations, aiTools } from '~/data/content';
import { orderFeatured, preloadImages } from '~/composables/useFeatured';

// Looping card clips. Muted and playsinline so autoplay is permitted at all,
// and started only while the card is actually on screen — this panel is one of
// six on the horizontal track, so an unconditional autoplay would decode and
// composite the whole time somebody was three sections away.
const videoEls = ref([]);
let videoObserver = null;

function watchVideos() {
  const items = (videoEls.value || []).filter(Boolean);
  if (!items.length) return;

  // Reduced motion: leave the poster frame showing rather than looping.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  videoObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const el = entry.target;
        if (entry.isIntersecting) {
          // First sighting is when the file is worth fetching at all.
          if (!el.src && el.dataset.src) el.src = el.dataset.src;
          // Rejected autoplay is not an error worth surfacing; the poster
          // simply stays put.
          el.play?.().catch(() => {});
        } else {
          el.pause?.();
        }
      });
    },
    { threshold: 0.25 }
  );

  items.forEach((el) => videoObserver.observe(el));
}

onMounted(watchVideos);

// How much bigger the hover preview is than the slot it covers. The one dial
// for this effect — the preview is always the largest box with the picture's
// own ratio that fits inside this multiple of the slot.
const POP_SCALE = 1.55;

// The small copy of a render, for anywhere on this page.
//
// The strip draws these at 189x141 and the hover preview at about 146x219,
// while the files in /images/ai are the full renders: 1024x1536 and up. That is
// 59 times more pixels than are ever put on screen, and six of them decoded to
// 35MB of bitmap as the panel scrolled into view, which is what made the
// desktop freeze mid-scroll. The full renders are still what /explore shows,
// because there they are actually displayed large.
const thumb = (src) => (src || '').replace('/images/ai/', '/images/ai/thumb/');

// The strip is one row of six. The full set lives on /explore, which is what
// the "View all" link beside it goes to — showing all of them here would run to
// three rows and push the tools out of the panel.
//
// The same six the rail would lead with, rather than the first six of the file.
// Featuring a picture is meant to be one decision about which work leads, and
// it was only reaching one of the two places that lead with it: a piece
// uploaded and featured showed on Explore and was still missing from the panel
// that sends people there.
//
// Newest first, because the heading says recent. That falls out of how the rows
// arrive: live ones lead and the compiled-in list follows, so a picture added
// today is at the front without anything having to sort by date.
const pieces = ref(aiGenerations);
const featuredPieces = computed(() => orderFeatured(pieces.value, aiGenerations));
const stripPieces = computed(() => featuredPieces.value.slice(0, 6));

// False until the six that will actually be shown are decoded and ready to
// appear at once. Until then the row is skeletons, which is the only way it
// never moves under anybody.
const ready = ref(false);

onMounted(async () => {
  try {
    const rows = await load('generations', aiGenerations);
    pieces.value = rows;

    await preloadImages(
      orderFeatured(rows, aiGenerations).slice(0, 6).map((piece) => thumb(piece.image))
    );
  } finally {
    // Whatever happened, the skeletons do not get to stay. A failed fetch shows
    // the compiled-in row, which is what it always fell back to.
    ready.value = true;
  }
});

// Sizes the preview to the picture's own aspect ratio, on the way in.
//
// Measured here rather than fixed in CSS for two reasons: the slot's rendered
// ratio is not always the 4/3 it asks for (max-height caps it on short
// screens), and the image's ratio is only known once it has loaded. Both are
// read at hover time, which costs one layout read on a deliberate gesture.
function sizePop(event) {
  const item = event.currentTarget;
  const slot = item.querySelector('.piece__link');
  const image = item.querySelector('.piece__image');
  const pop = item.querySelector('.piece__pop');
  if (!slot || !image || !pop) return;

  const rect = slot.getBoundingClientRect();
  if (!rect.width || !rect.height) return;

  // Fall back to the slot's own shape if the image has not loaded yet, so the
  // preview is never sized from a divide-by-zero.
  const ratio =
    image.naturalWidth && image.naturalHeight
      ? image.naturalWidth / image.naturalHeight
      : rect.width / rect.height;

  // The largest box with the picture's ratio that fits inside a box
  // POP_SCALE times the slot. Whichever axis runs out first is the one that
  // constrains it, so a tall image grows in height and a wide one in width,
  // and neither ends up bigger than intended on the other axis.
  const limitW = rect.width * POP_SCALE;
  const limitH = rect.height * POP_SCALE;

  let width = limitW;
  let height = limitW / ratio;
  if (height > limitH) {
    height = limitH;
    width = limitH * ratio;
  }

  pop.style.width = `${Math.round(width)}px`;
  pop.style.height = `${Math.round(height)}px`;
}

const NuxtLink = resolveComponent('NuxtLink');
const lockedStrand = ref(null);

// The gate remembers, so somebody who has already followed is not asked again
// every time they come back.
const { unlocked, restore } = useFollowGate();
onMounted(restore);

// Closed from the admin. This outranks the remembered follow: the point of the
// switch is to take the library down for everyone, including the people who
// unlocked it months ago.
const { load, useSettings } = useLiveContent();
const liveSettings = useSettings();
const libraryLocked = computed(() => liveSettings.value.libraryLocked === true);

// Two separate reasons a card does not open, and the switch does not depend on
// the follow gate being set up. Clearing `locked` in the data retires the
// follow gate, which is a fair thing to want, and must not also disable the
// admin's lock: the card would then link straight to a page that says it is
// closed.
const isGated = (discipline) =>
  (libraryLocked.value && discipline.href === '/library') ||
  (Boolean(discipline.locked) && !unlocked.value);

function ctaFor(discipline) {
  if (isGated(discipline)) return libraryLocked.value ? 'Currently unavailable' : 'Follow to unlock';
  return discipline.href === '/library' ? 'Open the library' : 'See the work';
}

onBeforeUnmount(() => {
  videoObserver?.disconnect();
  videoObserver = null;
});

// No per-tool filtering: every platform here does both image and video, so
// splitting them by medium would be misleading rather than useful.
</script>

<style lang="scss" scoped>
.ai-studio {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: clamp(1rem, 4vh, 3rem) 0;
  background: transparent;
  overflow: hidden;

  .container {
    width: 100%;
  }

  // Unpinned below this, so the padding is the only gap between sections.
  @media (max-width: 768px) {
    padding: clamp(3rem, 9vh, 4.5rem) 0;
  }
}

// Heading and the outbound link share a row, so the section states what it is
// and where it leads in one line instead of trailing off.
.ai-studio__head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
  margin-bottom: clamp(1rem, 3vh, 2.25rem);

  @media (max-width: 800px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
}

.section-heading {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  margin-bottom: clamp(0.5rem, 1.2vh, 0.75rem);

  .heading-line {
    // Hidden site-wide. Kept in the markup and styled so it can be brought
    // back by dropping this one line. display:none rather than opacity/width so
    // the flex gap beside it collapses too and the title sits flush left.
    display: none;
    width: 50px;
    height: 3px;
    flex-shrink: 0;
    // Tapered to a point at both ends and faded out, so it reads as a mark
    // rather than a hard bar. The clip gives the sharp tips; the gradient
    // stops the tips ending abruptly.
    background: linear-gradient(
      90deg,
      rgba(var(--accent-rgb), 0) 0%,
      var(--accent) 40%,
      var(--accent) 60%,
      rgba(var(--accent-rgb), 0) 100%
    );
    clip-path: polygon(0 50%, 14% 0, 86% 0, 100% 50%, 86% 100%, 14% 100%);
  }

  .section-title {
    font-size: clamp(1.5rem, 1.6vw + 1.6vh, 2.25rem);
    font-weight: 800;
    color: var(--text);
    margin: 0;
    font-family: 'Orbitron', sans-serif;
    letter-spacing: 4px;
    background: linear-gradient(135deg, var(--accent) 0%, rgba(var(--accent-rgb), 0.8) 50%, var(--text) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-transform: uppercase;
  }
}

.section-subtitle {
  color: var(--muted);
  font-size: clamp(0.8rem, 0.5vw + 0.8vh, 1rem);
  max-width: 620px;
  margin: 0;
}

.disciplines {
  list-style: none;
  margin: 0 0 clamp(1rem, 3vh, 2rem);
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: clamp(0.75rem, 1vw + 1vh, 1.5rem);

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
}

.disciplines > li {
  display: flex;
}

.discipline {
  // It's a <button> now: strip the UA chrome and let it fill the grid cell.
  width: 100%;
  text-align: left;
  font: inherit;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: clamp(0.9rem, 1vw + 1.2vh, 1.5rem);
  border: 1px solid rgba(var(--border-rgb), 0.3);
  border-radius: 16px;
  background: rgba(var(--muted-rgb), 0.03);
  position: relative;
  overflow: hidden;
  // The card is a link, so the global `a:hover { text-decoration: underline }`
  // would rule every line of copy inside it. Declared on the base and the hover
  // rather than only on .has-media, which left the plain card underlining.
  color: inherit;
  text-decoration: none;
  transition: border-color 0.2s ease-in-out, background 0.2s ease-in-out;

  &:hover,
  &:focus-visible {
    border-color: rgba(var(--accent-rgb), 0.35);
    background: rgba(var(--accent-rgb), 0.04);
    text-decoration: none;

    .discipline__more {
      color: var(--accent);

      svg {
        transform: translateX(3px);
      }
    }

    .discipline__media {
      transform: scale(1.06);
    }
  }

  // Picture-backed cards carry their own contrast, so the tinted surface and
  // the theme text colours are replaced rather than layered over the image.
  &.has-media {
    border-color: rgba(255, 255, 255, 0.12);
    text-decoration: none;

    .discipline__title {
  // Pushes the title and description down to sit against the bottom of the
  // card, filling the space the removed call to action left. On the panel card
  // the "Read more" line still sits under them.
  margin-top: auto; color: #fff; }
    .discipline__text { color: rgba(255, 255, 255, 0.72); }
    .discipline__index { color: rgba(255, 255, 255, 0.5); }
    .discipline__more { color: rgba(255, 255, 255, 0.85); }

    .discipline__icon {
      background: rgba(255, 255, 255, 0.14);
      color: #fff;
    }

    &:hover,
    &:focus-visible {
      border-color: rgba(255, 255, 255, 0.3);
      background: rgba(var(--muted-rgb), 0.03);
      text-decoration: none;

      .discipline__more { color: #fff; }
    }
  }
}

.discipline__media {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s cubic-bezier(0.33, 0, 0.2, 1);
}

// Dark enough for the copy to hold at any point in the picture, weighted to the
// bottom where the text sits.
.discipline__veil {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(8, 11, 16, 0.92) 0%,
    rgba(8, 11, 16, 0.72) 45%,
    rgba(8, 11, 16, 0.42) 100%
  );
}

// Everything above the picture. The card is a flex column, so these need to
// stay in flow while the media and veil sit behind them.
.discipline__top,
.discipline__title,
.discipline__text,
.discipline__more {
  position: relative;
  z-index: 1;
}

.discipline__more {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin-top: auto;
  padding-top: clamp(0.5rem, 1.4vh, 0.9rem);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--muted);
  transition: color 0.2s ease-in-out;

  svg {
    transition: transform 0.2s ease-in-out;
  }
}

.discipline__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: clamp(0.35rem, 1.2vh, 0.85rem);
}

.discipline__index {
  font-family: 'Orbitron', sans-serif;
  font-size: clamp(1.1rem, 0.8vw + 0.9vh, 1.6rem);
  font-weight: 700;
  color: rgba(var(--muted-rgb), 0.45);
  line-height: 1;
}

.discipline__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background:
    // Layered over the page colour rather than left translucent. At 10% alpha
    // the HoneycombField backdrop reads straight through the tile, which looks
    // like the comb is painted on top of it. Renders identically, but opaque.
    linear-gradient(rgba(var(--accent-rgb), 0.1), rgba(var(--accent-rgb), 0.1)),
    var(--background);
  color: var(--accent);
  flex-shrink: 0;
}

.discipline__title {
  font-size: clamp(0.9rem, 0.45vw + 0.75vh, 1.05rem);
  font-weight: 700;
  color: var(--text);
  margin: 0;
}

.discipline__text {
  font-size: clamp(0.75rem, 0.3vw + 0.65vh, 0.85rem);
  color: var(--muted);
  line-height: 1.55;
  margin: 0;
}

// Tools sit on their own full-width rule, so the row reads as a footer to the
// section rather than a half-empty second column.
.tools {
  display: flex;
  // Top, not centre: the logos wrap to a second row on a narrower screen, and
  // centring against a taller list dragged the label down with it.
  align-items: flex-start;
  gap: clamp(1rem, 3vw, 2.5rem);
  padding-top: clamp(0.85rem, 2.5vh, 1.5rem);
  border-top: 1px solid rgba(var(--border-rgb), 0.3);

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
}

.tools__label {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  flex-shrink: 0;
}

.tools__title {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--text);
}

.tools__note {
  font-size: 0.72rem;
  color: var(--muted);
}

.tools__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: clamp(0.4rem, 0.9vh, 0.7rem);
}


.tool {
  text-decoration: none;
  color: inherit;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.85rem 0.35rem 0.35rem;
  border: 1px solid rgba(var(--border-rgb), 0.3);
  border-radius: 999px;
  transition: border-color 0.2s ease-in-out, background 0.2s ease-in-out;

  &:hover {
    border-color: rgba(var(--accent-rgb), 0.4);
    background: rgba(var(--accent-rgb), 0.05);
    text-decoration: none;
  }

  &.is-static {
    cursor: default;
  }
}

// Logos come in at wildly different aspect ratios and padding, so they're
// boxed to a fixed square and contained rather than cropped.
.tool__logo {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: 50%;
  object-fit: contain;
  background: rgba(var(--muted-rgb), 0.08);
}

.tool__mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: 50%;
  font-family: 'Orbitron', sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  background:
    // Layered over the page colour rather than left translucent. At 10% alpha
    // the HoneycombField backdrop reads straight through the tile, which looks
    // like the comb is painted on top of it. Renders identically, but opaque.
    linear-gradient(rgba(var(--accent-rgb), 0.1), rgba(var(--accent-rgb), 0.1)),
    var(--background);
  color: var(--accent);
}

.tool__name {
  font-size: clamp(0.75rem, 0.28vw + 0.62vh, 0.88rem);
  font-weight: 600;
  color: var(--text);
  white-space: nowrap;
}

// The label holds a constant 332px, so between the stacking breakpoint and
// 1366px the logos are squeezed by exactly that and wrap to a second row.
// Trimmed rather than restacked, so the row still reads as one line.
@media (min-width: 901px) and (max-width: 1366px) {
  .tool {
    gap: 0.38rem;
    padding: 0.3rem 0.6rem 0.3rem 0.3rem;
  }

  .tool__logo,
  .tool__mark {
    width: 21px;
    height: 21px;
  }

  .tool__name {
    font-size: 0.72rem;
  }
}

.work {
  margin-bottom: clamp(0.85rem, 2.5vh, 1.6rem);
}

.work__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: clamp(0.45rem, 1.2vh, 0.75rem);
}

.work__title {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--text);
}

.work__all {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--muted);
  text-decoration: none;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: var(--accent);
    text-decoration: none;
  }
}

.work__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: clamp(0.4rem, 0.8vw, 0.75rem);

  @media (max-width: 900px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.piece {
  position: relative;

  // Raised only while hovered, so the preview is never tucked behind the card
  // sitting next to it. Off again the moment the pointer leaves, so it does not
  // permanently reorder the grid.
  &:hover {
    z-index: 5;
  }
}

// Hover preview. Hidden entirely without a pointer to summon it — on touch the
// card keeps its existing behaviour and none of this is rendered or animated.
.piece__pop {
  display: none;
  position: absolute;
  top: 50%;
  left: 50%;
  // Sized in JS on pointerenter; this is only the pre-measurement fallback.
  width: 120%;
  height: auto;
  // Starts a little further back than it used to. The extra travel is what
  // makes the easing readable — at 0.94 the curve had almost no distance to
  // act over, so it read as a snap however it was timed.
  transform: translate(-50%, -50%) scale(0.9);
  opacity: 0;
  // Hover belongs to the card underneath. Without this the preview would sit
  // under the cursor, trigger its own leave, and flicker.
  pointer-events: none;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(var(--accent-rgb), 0.45);
  background: var(--background);
  box-shadow: 0 20px 44px rgba(0, 0, 0, 0.3);
  // easeInOutCubic. The keyword `ease-in-out` front-loads very little and
  // arrives abruptly at this distance; this eases away from and into both ends,
  // which is what stops it feeling like a snap. Longer, too — 0.22s was not
  // enough room for any curve to be felt.
  transition:
    opacity 0.36s cubic-bezier(0.65, 0, 0.35, 1),
    transform 0.36s cubic-bezier(0.65, 0, 0.35, 1);
}

.piece__pop-image {
  display: block;
  width: 100%;
  height: 100%;
  // The box is already the picture's own ratio, so this crops nothing; it just
  // avoids a sub-pixel letterbox at the edges.
  object-fit: cover;
}

.piece__pop-meta {
  position: absolute;
  inset: auto 0 0 0;
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
  padding: 0.6rem 0.55rem 0.45rem;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.78), rgba(0, 0, 0, 0));
}

@media (hover: hover) {
  .piece__pop {
    display: block;
  }

  .piece:hover .piece__pop {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }

  // The slot's own caption and zoom both belong to the no-preview behaviour:
  // the caption would sit beside the preview saying the same thing twice, and
  // the zoom is a cropped version of what the preview already shows properly.
  //
  // Qualified through .piece__link on purpose. The rules that turn these on
  // live further down the file at equal specificity, so matching them is not
  // enough to win — this needs one more level.
  .piece:hover .piece__link .piece__meta {
    opacity: 0;
  }

  .piece:hover .piece__link .piece__image {
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .piece__pop {
    transition: opacity 0.15s ease-in-out;
    transform: translate(-50%, -50%);
  }

  .piece:hover .piece__pop {
    transform: translate(-50%, -50%);
  }
}

.piece__link {
  position: relative;
  display: block;
  // Width and height are set independently on purpose.
  //
  // This was `aspect-ratio: 4/3` with a `max-height` in vh, which meant the
  // height cap also capped the width: on a laptop viewport (a 768px screen is
  // nearer 640px of viewport once browser chrome is taken off) the card came
  // out well narrower than its grid cell. The leftover read as oversized gaps,
  // the row no longer lined up with the heading above it, and the hover preview
  // sat right of the picture.
  //
  // Filling the cell and clamping the height instead keeps the row flush at
  // every size. The picture is object-fit: cover, so it crops rather than
  // distorting as the ratio moves.
  width: 100%;
  height: clamp(110px, 17vh, 165px);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(var(--border-rgb), 0.3);
  background: rgba(var(--muted-rgb), 0.06);
  text-decoration: none;

  &:hover {
    border-color: rgba(var(--accent-rgb), 0.45);
    text-decoration: none;

    .piece__image {
      transform: scale(1.05);
    }

    .piece__meta {
      opacity: 1;
    }
  }
}

.piece__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.25s ease-in-out;
}

// Holds a slot at the size the picture will take, so the row is its final
// height before anything is in it.
.piece__link--skeleton {
  border-radius: 10px;
}

.piece__placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-family: 'Orbitron', sans-serif;
  font-size: 1.1rem;
  font-weight: 700;
  color: rgba(var(--accent-rgb), 0.7);
  background: rgba(var(--accent-rgb), 0.07);
}

.piece__meta {
  position: absolute;
  inset: auto 0 0 0;
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
  padding: 0.6rem 0.55rem 0.45rem;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.78), rgba(0, 0, 0, 0));
  opacity: 0;
  transition: opacity 0.25s ease-in-out;
}

.piece__name {
  font-size: 0.68rem;
  font-weight: 700;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}



// Section headings step down noticeably on phones: the desktop clamp is tuned
// for a 100vh panel and reads oversized on a narrow screen.
@media (max-width: 768px) {
  .section-heading {
    gap: 0.6rem;

    .heading-line {
      width: 26px;
      height: 2px;
    }

    .section-title {
      font-size: clamp(0.95rem, 4.2vw, 1.25rem);
      letter-spacing: 2px;
    }
  }
}
</style>
